/**
 * ConfirmationStep — Booking confirmation screen
 *
 * On-brand design with club logo, green accent, and clear status messaging.
 */

import { CheckCircle, Mail, Calendar } from '../components/icons'

interface ConfirmationStepProps {
  bookingReference: string
  clubName: string
  status: string
  onReset?: () => void
  brandLogoUrl?: string
}

export function ConfirmationStep({ bookingReference, clubName, status, onReset, brandLogoUrl }: ConfirmationStepProps) {
  const isPending = status.includes('PENDING')

  return (
    <div className="bw2-confirmation" role="status" aria-label={isPending ? 'Booking submitted, awaiting confirmation' : 'Booking confirmed'}>
      {/* Brand logo */}
      {brandLogoUrl && (
        <div style={{ marginBottom: '12px' }}>
          <img
            src={brandLogoUrl}
            alt={clubName}
            style={{ maxWidth: '80px', maxHeight: '60px', objectFit: 'contain' }}
          />
        </div>
      )}

      {/* Success icon */}
      <div className="bw2-confirmation__icon" aria-hidden="true">
        <CheckCircle size={22} color="var(--bw2-accent)" />
      </div>

      <h2 className="bw2-confirmation__title">
        {isPending ? 'Tee Time Requested' : 'Confirmed'}
      </h2>

      <p className="bw2-confirmation__subtitle">
        {isPending
          ? `Your tee time request at ${clubName} has been submitted.`
          : `Your tee time at ${clubName} is confirmed.`}
      </p>

      {/* Booking reference */}
      <div className="bw2-confirmation__ref" aria-label={`Booking reference: ${bookingReference}`}>
        {bookingReference}
      </div>

      {/* Status badge */}
      <div style={{ marginTop: '12px' }}>
        <span className={`bw2-badge ${isPending ? 'bw2-badge--info' : 'bw2-badge--success'}`}>
          {isPending ? 'Awaiting Final Confirmation' : 'Confirmed'}
        </span>
      </div>

      {/* Next steps */}
      <div style={{
        marginTop: '16px', textAlign: 'left',
        background: 'var(--bw2-bg-surface)', border: '1px solid var(--bw2-border-subtle)',
        borderRadius: '4px', padding: '10px',
      }} role="region" aria-label="What happens next">
        <div style={{
          fontSize: '9px', fontWeight: 600, color: 'var(--bw2-text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '8px',
          fontFamily: 'var(--bw2-font-label)',
        }}>
          What happens next
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <Mail size={12} style={{ color: 'var(--bw2-accent)', marginTop: '1px', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--bw2-text)' }}>
                Confirmation email sent
              </div>
              <div style={{ fontSize: '9px', color: 'var(--bw2-text-secondary)' }}>
                Check your inbox for booking details
              </div>
            </div>
          </div>

          {isPending && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <Calendar size={12} style={{ color: 'var(--bw2-accent)', marginTop: '1px', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--bw2-text)' }}>
                  Club confirmation
                </div>
                <div style={{ fontSize: '9px', color: 'var(--bw2-text-secondary)' }}>
                  {clubName} will confirm your tee time within 48 hours
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={{ marginTop: '12px', display: 'flex', gap: '4px', justifyContent: 'center' }}>
        {onReset && (
          <button className="bw2-btn bw2-btn--primary" onClick={onReset} type="button">
            Book Another Tee Time
          </button>
        )}
      </div>
    </div>
  )
}
